import { useIntl } from 'react-intl';

import type { Flag } from '@townsquare/flags/types';
import { useAdminHubLinkForSite, useBillingPreviewUrl } from '@townsquare/url-utils';

import { messages } from './messages';

const SUPPORT_CONTACT_URL = 'https://support.atlassian.com/contact/';

import type { InviteToProjectResult } from './useInviteToProject';

export const useGetInviteToProjectFlags = () => {
	const { formatMessage } = useIntl();
	const adminUsersUrl = `${useAdminHubLinkForSite()}/users`;
	const adminBillingUrl = useBillingPreviewUrl();

	return (result: InviteToProjectResult, isSiteAdmin: boolean = false): Flag[] => {
		const { successfulInvites, failedInvites } = result;
		const finalFlags: Flag[] = [];

		if (failedInvites.length > 0) {
			const errorFlag = determinePrimaryErrorFlag(result, isSiteAdmin, formatMessage, adminBillingUrl);
			finalFlags.push(errorFlag);

			if (successfulInvites.length === 0) {
				return finalFlags;
			}
		}

		const { invitedEmails, requestAccessCount } = successfulInvites.reduce(
			(acc, invite) => {
				const status = invite.response.inviteStatus;
				if (status === 'INVITED' || status === 'USER_EXISTS') {
					acc.invitedEmails.push(invite.email);
				} else if (status === 'INVITED_PENDING_APPROVAL' || status === 'PENDING_INVITE_EXISTS') {
					acc.requestAccessCount += 1;
				}
				return acc;
			},
			{ invitedEmails: [] as string[], requestAccessCount: 0 },
		);

		if (requestAccessCount > 0) {
			finalFlags.push(buildRequestAccessSuccessFlag(formatMessage, requestAccessCount));
			return finalFlags;
		}

		if (invitedEmails.length > 0) {
			finalFlags.push(buildInviteSuccessFlag(formatMessage, invitedEmails, isSiteAdmin, adminUsersUrl));
		}

		return finalFlags;
	};
};

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const determinePrimaryErrorFlag = (
	result: InviteToProjectResult,
	isSiteAdmin: boolean,
	formatMessage: FormatMessage,
	adminBillingUrl: string,
): Flag => {
	const { failedInvites, successfulInvites } = result;

	const { userLimitEmails, standardEmails, requestAccessEmails } = failedInvites.reduce(
		(acc, failed) => {
			if (failed.status === 'LICENSE_LIMIT_REACHED') {
				acc.userLimitEmails.push(failed.email);
			} else if (failed.status === 'NOT_INVITED' || failed.error) {
				acc.standardEmails.push(failed.email);
			} else if (failed.status) {
				acc.requestAccessEmails.push(failed.email);
			}
			return acc;
		},
		{ userLimitEmails: [] as string[], standardEmails: [] as string[], requestAccessEmails: [] as string[] },
	);

	const noSuccesses = successfulInvites.length === 0;
	const hasInviteErrors = standardEmails.length > 0 || userLimitEmails.length > 0;
	const hasRequestAccessErrors = requestAccessEmails.length > 0;

	if (noSuccesses && hasInviteErrors && hasRequestAccessErrors) {
		return buildAllRequestAccessAndInviteFailedFlag(formatMessage, isSiteAdmin);
	}

	if (userLimitEmails.length > 0) {
		return buildInviteFailedUserLimitFlag(formatMessage, userLimitEmails, isSiteAdmin, adminBillingUrl);
	}

	if (standardEmails.length > 0) {
		return buildInviteFailedFlag(formatMessage, standardEmails);
	}

	if (requestAccessEmails.length > 0) {
		return buildRequestAccessFailureFlag(formatMessage, requestAccessEmails);
	}

	return buildErrorFlag(formatMessage, isSiteAdmin);
};

const buildErrorFlag = (formatMessage: FormatMessage, isSiteAdmin: boolean): Flag => ({
	id: 'generic-error-flag',
	type: 'error',
	shouldAutoDismiss: false,
	title: formatMessage(messages.errorFlagTitle),
	description: isSiteAdmin
		? formatMessage(messages.errorFlagDescriptionAdmin)
		: formatMessage(messages.errorFlagDescription),
});

const buildAllRequestAccessAndInviteFailedFlag = (formatMessage: FormatMessage, isSiteAdmin: boolean): Flag => ({
	id: 'all-request-access-and-invite-failed-flag',
	type: 'error',
	shouldAutoDismiss: false,
	title: formatMessage(messages.allRequestAccessAndInviteFailedFlagTitle),
	description: formatMessage(messages.allRequestAccessAndInviteFailedFlagDescription, { isSiteAdmin }),
});

const buildRequestAccessFailureFlag = (formatMessage: FormatMessage, emails: string[]): Flag => ({
	id: 'request-access-failure-flag',
	type: 'error',
	shouldAutoDismiss: false,
	title: formatMessage(messages.requestAccessFailureFlagTitle),
	description:
		emails.length === 1
			? formatMessage(messages.requestAccessFailureFlagOneRequestFailed, { email: emails[0] })
			: formatMessage(messages.requestAccessFailureFlagMultiRequestFailed, { count: emails.length }),
});

const buildInviteSuccessFlag = (
	formatMessage: FormatMessage,
	invitedEmails: string[],
	isSiteAdmin: boolean,
	adminUsersUrl: string,
): Flag => {
	const invitedCount = invitedEmails.length;

	return {
		id: 'invite-success-flag',
		type: 'success',
		title: formatMessage(messages.inviteSuccessFlagTitle, { invitedCount }),
		description:
			invitedCount === 1
				? formatMessage(messages.inviteSuccessFlagOneInviteSent, { email: invitedEmails[0] })
				: formatMessage(messages.inviteSuccessFlagMultiInviteSent, { invitedCount }),
		actions: isSiteAdmin
			? [
					{
						content: formatMessage(messages.inviteSuccessFlagViewUsers),
						onClick: () => window.open(adminUsersUrl, '_blank'),
					},
				]
			: [],
	};
};

const buildRequestAccessSuccessFlag = (formatMessage: FormatMessage, requestAccessCount: number): Flag => ({
	id: 'request-access-success-flag',
	type: 'warning',
	title: formatMessage(messages.requestAccessSuccessFlagTitle, { count: requestAccessCount }),
	description: formatMessage(messages.requestAccessSuccessFlagDescription),
});

const buildInviteFailedFlag = (formatMessage: FormatMessage, emails: string[]): Flag => {
	const failedCount = emails.length;

	return {
		id: 'invite-failed-flag',
		type: 'error',
		shouldAutoDismiss: false,
		title: formatMessage(messages.inviteFailedFlagTitle, { failedCount }),
		description:
			failedCount === 1
				? formatMessage(messages.inviteFailedFlagOneInviteFailed, { email: emails[0] })
				: formatMessage(messages.inviteFailedFlagManyInvitesFailed),
		actions: [
			{
				content: formatMessage(messages.inviteFailedFlagContactSupport),
				onClick: () => window.open(SUPPORT_CONTACT_URL, '_blank'),
			},
		],
	};
};

const getInviteFailedUserLimitDescription = (
	formatMessage: FormatMessage,
	emails: string[],
	isSiteAdmin: boolean,
	failedCount: number,
): string => {
	if (!isSiteAdmin) {
		return formatMessage(messages.inviteFailedUserLimitFlagInviteFailedTrustedUsers, { failedCount });
	}
	if (failedCount === 1) {
		return formatMessage(messages.inviteFailedUserLimitFlagOneInviteFailedAdmin, { email: emails[0] });
	}
	return formatMessage(messages.inviteFailedUserLimitFlagManyInvitesFailedAdmin, { failedCount });
};

const buildInviteFailedUserLimitFlag = (
	formatMessage: FormatMessage,
	emails: string[],
	isSiteAdmin: boolean,
	adminBillingUrl: string,
): Flag => {
	const failedCount = emails.length;

	return {
		id: 'invite-failed-user-limit-reached-flag',
		type: 'error',
		shouldAutoDismiss: false,
		title: formatMessage(messages.inviteFailedUserLimitFlagTitle, { failedCount }),
		description: getInviteFailedUserLimitDescription(formatMessage, emails, isSiteAdmin, failedCount),
		actions: isSiteAdmin
			? [
					{
						content: formatMessage(messages.inviteFailedUserLimitFlagUpgradePlan),
						onClick: () => window.open(adminBillingUrl, '_blank'),
					},
					{
						content: formatMessage(messages.inviteFailedUserLimitFlagContactSupport),
						onClick: () => window.open(SUPPORT_CONTACT_URL, '_blank'),
					},
				]
			: [],
	};
};
